'use client';

import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { PermissionSetDomainAction } from '@squareup/dex-types-shared-rbac-permissions';
import { useToaster } from '@squareup/dex-ui-shared-market';
import { getRolesForDomainAction } from '@squareup/dex-utils-shared-rbac-permissions';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';

type LoadingNoOp = () => void;
type ShowPermissionErrorToast = () => void;

interface RbacPermissionCheckResultLoading {
  action: LoadingNoOp;
  isLoading: true;
  isPermitted: false;
}

interface RbacPermissionCheckResultLoaded<T> {
  action: T | ShowPermissionErrorToast;
  isLoading: false;
  isPermitted: boolean;
}

type RbacPermissionCheckResult<T> =
  | RbacPermissionCheckResultLoading
  | RbacPermissionCheckResultLoaded<T>;

interface UseRbacActionGuardProps<T> {
  action: T;
  actionName: PermissionSetDomainAction;
  skip?: boolean;
}

const loadingNoOp: LoadingNoOp = () => {};

/**
 * Requires the user to have the given RBAC permission to execute the given
 * action.
 *
 * @param action The action that is returned if the user has the given RBAC
 * permission or the feature flag is disabled.
 * @param actionName The name of the RBAC permission to check.
 *
 * @returns An object containing `action`, `isLoading`, and `isPermitted`
 * properties. While the RBAC permission is being checked, the returned
 * `action` is a no-op. If the RBAC permission is not granted, the returned
 * `action` will show an error toast.
 */
const useRbacActionGuard = <T>({
  action,
  actionName,
  skip = false,
}: UseRbacActionGuardProps<T>): RbacPermissionCheckResult<T> => {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  const showPermissionErrorToast = useCallback(() => {
    showToast({
      message: t('permissions:insufficient-permissions-action-error-message'),
      variant: 'critical',
    });
  }, [showToast, t]);

  const {
    data: userRbacPermissionsData,
    isLoading: userRbacPermissionsIsLoading,
    isUninitialized: userRbacPermissionsIsUninitialized,
  } = getDeveloperApi().useGetUserRbacPermissionsQuery(undefined, {
    skip,
  });

  if (userRbacPermissionsIsLoading || userRbacPermissionsIsUninitialized) {
    return {
      action: loadingNoOp,
      isLoading: true,
      isPermitted: false,
    };
  }

  let isPermitted = false;
  if (
    userRbacPermissionsData?.permissions &&
    userRbacPermissionsData.permissions.length > 0
  ) {
    isPermitted = getRolesForDomainAction(actionName).every((role) =>
      userRbacPermissionsData?.permissions.includes(role)
    );
  }

  return {
    action: isPermitted ? action : showPermissionErrorToast,
    isLoading: false,
    isPermitted,
  };
};

export { loadingNoOp, useRbacActionGuard };
